import { BASE_PATH } from '../../constants/paths';

type LinkShape = {
  href: string;
  target: '_blank' | '_self';
  rel?: 'nofollow';
  referrerPolicy?: 'unsafe-url';
};

/**
 * URLs which follow the same /blog pattern but aren't owned by this Next.js application
 * Treat them as non-blog internal paths
 */
const nonNextBlogPaths = ['/blog/updates'];

/**
 * Given an href (not config.basePath aware) string output appropriate href, target, and rel
 */
export const urlParser = (href: string): LinkShape => {
  const isBlogUrl =
    // Fully qualified internal blog URLs
    /zapier(?:-staging)?[.]com[/]blog[/]?/i.test(href) ||
    // Absolute Blog URLs
    href.startsWith(BASE_PATH);

  if (isBlogUrl) {
    /**
     * limiting to a max of 36 characters to avoid catastrophic backtracking
     * which could lead to a denial of service attack. (https://www.regular-expressions.info/catastrophic.html)
     */
    const path = href.replace(/.{1,36}?[.]com([/]blog.*)/, '$1');

    const isNonNextBlogPath = nonNextBlogPaths.some((nonNextBlogPath) =>
      path.startsWith(nonNextBlogPath),
    );
    // Open all non-blog internal paths in new tab
    const target = isNonNextBlogPath ? '_blank' : '_self';

    return { href: path, target };
  }
  const isZapierSubdomain =
    /**
     * fully qualified internal subdomains of Zapier
     * limiting to a max of 30 characters to avoid catastrophic backtracking
     * which could lead to a denial of service attack. (https://www.regular-expressions.info/catastrophic.html)
     */
    /([a-z0-9A-Z]{1,30}?\.zapier\.com)/.test(href);

  // handle subdomains of Zapier
  if (isZapierSubdomain) {
    return { href, target: '_blank', referrerPolicy: 'unsafe-url' };
  }
  const isInternalUrl =
    // Fully qualified internal non-blog URLs
    /(?:zapier|zapier-staging)[.]com/i.test(href) ||
    // Absolute URLs
    href.startsWith('/');

  if (isInternalUrl) {
    return { href, target: '_blank' };
  }

  // Handle hash-only URLs
  if (href.startsWith('#')) {
    return { href, target: '_self' };
  }

  // Fallback behavior
  return { href, rel: 'nofollow', target: '_blank' };
};
